import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { apiRequest } from "@/lib/queryClient";
import { captureEvent } from "@/lib/posthog";

export type WaitlistAudience = "team" | "individual";

interface WaitlistFormProps {
  /** Which branch opens first. The homepage leads with individuals, /teams with teams. */
  defaultAudience: WaitlistAudience;
  /** Target pre-filled from the hero goal box (individuals branch). */
  prefilledTarget?: string;
  /** Where the submission came from, recorded on the contact and in analytics. */
  sourcePage: "home" | "teams";
  /** Opens the access-key dialog for already-invited users. */
  onAccessKeyClick: () => void;
}

const ROLES = ["Team lead", "Manager", "Director", "Founder / C-level", "Other"];
const TEAM_SIZES = ["2–4", "5–15", "16–50", "50+"];
const SPRINT_SOURCES = [
  "Onboarding docs",
  "SOPs / process docs",
  "Product or tool training",
  "AI-readiness",
  "Something else",
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function WaitlistForm({
  defaultAudience,
  prefilledTarget,
  sourcePage,
  onAccessKeyClick,
}: WaitlistFormProps) {
  const [audience, setAudience] = useState<WaitlistAudience>(defaultAudience);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [role, setRole] = useState("");
  const [teamSize, setTeamSize] = useState("");
  const [sprintSource, setSprintSource] = useState("");
  const [target, setTarget] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const startedRef = useRef(false);
  const targetRef = useRef<HTMLInputElement>(null);

  // The hero goal box hands its value over and switches the form to the
  // individual branch; focus follows so the visitor can keep typing.
  useEffect(() => {
    if (!prefilledTarget) return;
    setAudience("individual");
    setTarget(prefilledTarget);
    targetRef.current?.focus({ preventScroll: true });
  }, [prefilledTarget]);

  const markStarted = () => {
    if (startedRef.current) return;
    startedRef.current = true;
    captureEvent("waitlist_form_started", { audience, source_page: sourcePage });
  };

  const chooseAudience = (next: WaitlistAudience) => {
    setAudience(next);
    setError(null);
    captureEvent("waitlist_branch_selected", { audience: next, source_page: sourcePage });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!EMAIL_PATTERN.test(email.trim())) {
      setError("Please enter a valid email address.");
      return;
    }
    if (!name.trim()) {
      setError("Please tell us your name.");
      return;
    }
    if (audience === "team" && !company.trim()) {
      setError("Please tell us your company name.");
      return;
    }

    setSubmitting(true);
    try {
      await apiRequest("/api/waitlist/submit", {
        method: "POST",
        body: JSON.stringify({
          audience,
          email: email.trim(),
          name: name.trim(),
          sourcePage,
          ...(audience === "team"
            ? {
                company: company.trim(),
                role,
                teamSize,
                sprintSource,
              }
            : { target: target.trim() }),
        }),
      });
      captureEvent("waitlist_submitted", {
        audience,
        source_page: sourcePage,
        ...(audience === "team" ? { team_size: teamSize, role } : {}),
      });
      setSubmitted(true);
    } catch (submitError) {
      const message =
        submitError instanceof Error && submitError.message
          ? submitError.message
          : "Something went wrong. Please try again.";
      setError(message);
      captureEvent("waitlist_submit_failed", { audience, source_page: sourcePage });
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="form-card" data-testid="waitlist-success">
        <div className="f-success">
          <div className="tick" aria-hidden="true">✓</div>
          <h3>You're on the list.</h3>
          <p>
            We open the doors in small groups. When your turn comes we'll email you personally
            to get you started. No spam in the meantime, promise.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form className="form-card" onSubmit={handleSubmit} onFocus={markStarted} data-testid="waitlist-form">
      <div className="branch" role="group" aria-label="Who is this for">
        <button
          type="button"
          className={`br-opt solo${audience === "individual" ? " on" : ""}`}
          onClick={() => chooseAudience("individual")}
          aria-pressed={audience === "individual"}
          data-testid="branch-individual"
        >
          Just me
          <small>I'm learning for myself</small>
        </button>
        <button
          type="button"
          className={`br-opt team${audience === "team" ? " on" : ""}`}
          onClick={() => chooseAudience("team")}
          aria-pressed={audience === "team"}
          data-testid="branch-team"
        >
          For my team
          <small>I lead or manage people</small>
        </button>
      </div>

      <div className="f-field">
        <label htmlFor="wl-email">{audience === "team" ? "Work email" : "Email"}</label>
        <input
          id="wl-email"
          type="email"
          autoComplete="email"
          required
          placeholder={audience === "team" ? "you@company.com" : "you@example.com"}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          data-testid="input-waitlist-email"
        />
      </div>

      <div className="f-field">
        <label htmlFor="wl-name">Name</label>
        <input
          id="wl-name"
          type="text"
          autoComplete="name"
          required
          placeholder="Your name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          data-testid="input-waitlist-name"
        />
      </div>

      {audience === "team" ? (
        <>
          <div className="f-field">
            <label htmlFor="wl-company">Company</label>
            <input
              id="wl-company"
              type="text"
              autoComplete="organization"
              required
              placeholder="Company name"
              value={company}
              onChange={(event) => setCompany(event.target.value)}
              data-testid="input-waitlist-company"
            />
          </div>
          <div className="f-field">
            <label htmlFor="wl-role">Your role</label>
            <select
              id="wl-role"
              value={role}
              onChange={(event) => setRole(event.target.value)}
              data-testid="select-waitlist-role"
            >
              <option value="">Select your role</option>
              {ROLES.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
          <div className="f-field">
            <label htmlFor="wl-team-size">Team size</label>
            <select
              id="wl-team-size"
              value={teamSize}
              onChange={(event) => setTeamSize(event.target.value)}
              data-testid="select-waitlist-team-size"
            >
              <option value="">Select team size</option>
              {TEAM_SIZES.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
          <div className="f-field">
            <label htmlFor="wl-source">You'd build a sprint from…</label>
            <select
              id="wl-source"
              value={sprintSource}
              onChange={(event) => setSprintSource(event.target.value)}
              data-testid="select-waitlist-sprint-source"
            >
              <option value="">Select one</option>
              {SPRINT_SOURCES.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
        </>
      ) : (
        <div className="f-field">
          <label htmlFor="wl-target">Your target</label>
          <input
            id="wl-target"
            ref={targetRef}
            type="text"
            placeholder="What do you want to get better at?"
            value={target}
            onChange={(event) => setTarget(event.target.value)}
            data-testid="input-waitlist-target"
          />
        </div>
      )}

      {error && (
        <div className="f-error" role="alert" data-testid="waitlist-error">
          {error}
        </div>
      )}

      <p className="f-consent">
        By joining, you agree we can email you about early access. We keep what you enter here,
        we never sell it, and you can unsubscribe or ask us to delete it at any time. More in our{" "}
        <Link to="/privacy">Privacy Policy</Link>.
      </p>

      <button type="submit" className="btn pri form-cta" disabled={submitting} data-testid="button-waitlist-submit">
        {submitting ? "Joining…" : "Join the waitlist"}
      </button>

      <p className="f-links">
        Have an access key?{" "}
        <button type="button" className="linklike" onClick={onAccessKeyClick} data-testid="link-access-key">
          Enter it here
        </button>{" "}
        · Already on board? <Link to="/login">Sign in</Link>
      </p>
    </form>
  );
}
